<template>
	<div>
		<div>
			<CInput
				v-model.trim="shippingMethod"
				placeholder="Search by shipping method"
				class="flex-fill"
				autocomplete="off"
				data-test-id="input-shipping-method"
			>
				<template #prepend-content>
					<CIcon class="cil-magnifying-glass" name="cil-magnifying-glass" />
				</template>
			</CInput>
		</div>
		<slot
			v-if="!filteredList.length"
			name="no-item"
		>
		</slot>
		<div v-show="filteredList.length" class="list">
			<div class="select-all mb-1 align-items-center">
				<CInputCheckbox
					:checked.sync="isSelectedAll"
					custom
					label="Select all"
					class="select-all-input"
					add-label-classes="font-weight-bold"
					data-test-id="select-all-input"
					@update:checked="handleCheckedSelectAll"
				/>
			</div>
			<DynamicScroller
				:items="filteredList"
				:min-item-size="35"
				key-field="id"
				class="scroller list-select"
			>
				<template v-slot="{ item, index, active }">
					<DynamicScrollerItem
						:item="item"
						:active="active"
						:data-index="index"
						class="list-item"
					>
						<div class="d-flex flex-fill">
							<CInputCheckbox
								:id="`item-shipping${item.id}`"
								:checked="checkItemIsSelected(item.id)"
								custom
								class="list-checkbox overflow-hidden"
								@update:checked="handleCheckedShippingMethod($event, item)"
							>
								<template #label>
									<label :for="`item-shipping${item.id}`" class="custom-control-label list-label w-100 typo-body-2 font-weight-normal text-nowrap text-truncate color-black-45 mb-0 ml-2">
										<span class="list-shipping-method color-black">{{ item.name }}</span>
									</label>
								</template>
							</CInputCheckbox>
						</div>
					</DynamicScrollerItem>
				</template>
			</DynamicScroller>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

export default {
	name: 'FormGetShippingMethod',
	components: {
		DynamicScroller,
		DynamicScrollerItem,
	},
	props: {
		selectedList: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			isSelectedAll: false,
			shippingMethod: '',
			shippingMethodList: [],
		};
	},
	computed: {
		...mapGetters({
			getShippingMethodList: 'shippings/shippingMethodList',
		}),
		filteredList() {
			if (!this.shippingMethod) {
				return this.shippingMethodList;
			}

			return this.shippingMethodList.filter((method) => method.name.toLowerCase().startsWith(this.shippingMethod.toLowerCase()));
		},
	},
	watch: {
		filteredList() {
			this.checkSelectionState();
		},

		selectedList() {
			this.checkSelectionState();
		},
	},
	async created() {
		await this.getShippingMethods();
		this.shippingMethodList = this.getShippingMethodList;
	},
	methods: {
		...mapActions({
			getShippingMethods: 'shippings/getShippingMethodList',
		}),
		resetState() {
			this.shippingMethod = '';
		},
		checkItemIsSelected(val) {
			return this.selectedList.some((item) => item.id === val);
		},
		checkSelectionState() {
			const isEverySelected = this.filteredList.length && this.filteredList.every((method) => this.selectedList.map((item) => item.id).includes(method.id));
			const isSomeSelected = this.filteredList.length && this.filteredList.some((method) => this.selectedList.map((item) => item.id).includes(method.id));

			if (isEverySelected) {
				this.setSelectedAllState('checked');
				return;
			}

			if (isSomeSelected) {
				this.setSelectedAllState('indeterminated');
				return;
			}

			this.setSelectedAllState('unchecked');
		},
		setSelectedAllState(state) {
			let isChecked = false;
			let isIndeterminate = false;
			switch (state) {
				case 'checked': {
					isChecked = true;
					isIndeterminate = false;
					break;
				}
				case 'unchecked': {
					isChecked = false;
					isIndeterminate = false;
					break;
				}
				case 'indeterminated': {
					isChecked = true;
					isIndeterminate = true;
					break;
				}
				default: break;
			}

			const selectAllEle = this.$el.querySelector('.select-all-input input');
			if (selectAllEle) {
				selectAllEle.checked = isChecked;
				selectAllEle.indeterminate = isIndeterminate;
			}
		},
		handleCheckedSelectAll() {
			if (this.isSelectedAll) {
				const selectedItems = this.filteredList;
				this.addToSelectedList(selectedItems);
			} else {
				const selectedItems = this.filteredList.map((item) => item.id);
				this.removeFromSelectedList(selectedItems);
			}
		},
		handleCheckedShippingMethod(checked, method) {
			const isSelected = this.selectedList.some((item) => item.id === method.id);
			if (!isSelected) {
				this.addToSelectedList(method);
			} else {
				this.removeFromSelectedList(method.id);
			}
		},

		/**
		 * @param {String|Array} items Method or List of shipping method
		 */
		addToSelectedList(items = []) {
			let newItems = items;
			if (!Array.isArray(items)) {
				newItems = [items];
			}
			const uniqueItems = [...new Set([...this.selectedList, ...newItems])].sort();
			this.$emit('onChange', uniqueItems);
		},

		/**
		 * @param {String|Array} items Method or List of shipping method
		 */
		removeFromSelectedList(items = []) {
			let itemsToRemove = items;
			if (!Array.isArray(items)) {
				itemsToRemove = [items];
			}

			this.$emit('onChange', this.selectedList.filter((selectedItem) => !itemsToRemove.includes(selectedItem.id)));
		},
	},
};
</script>

<style lang="scss" scoped>
	.scroller {
		height: 100%;
	}

	.btn-add {
		min-width: rem(84);
	}

	.list {
		margin: 0 -#{rem(16)} -#{rem(16)};
	}

	.select-all {
		background-color: $color-gray-100;
		padding: rem(8) rem(16);
	}

	.list-select {
		min-height: rem(360);
		max-height: rem(360);
		list-style: none;
		padding: 0;
		margin: 0;

		.list-item {
			padding: rem(6) rem(16);

			label {
				margin-top: rem(3);
				cursor: pointer;
			}
		}
	}
</style>
